import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./RightSidebar.module.css";

import {
  useFollowUserMutation,
  useUnfollowUserMutation,
  useGetUsersByFollowersQuery
} from "../store/api/followApi";

export default function RightSidebar() {

  const navigate = useNavigate();

  const { data: users = [], isLoading } = useGetUsersByFollowersQuery();

  const [followUser] = useFollowUserMutation();
  const [unfollowUser] = useUnfollowUserMutation();

  const [loadingUser, setLoadingUser] = useState(null);

  // API users
  const [localUsers, setLocalUsers] = useState([]);

  // locally followed users to prevent instant disappearance
  const [followedUsers, setFollowedUsers] = useState([]);

  // sync API users
 useEffect(() => {
  if (JSON.stringify(localUsers) !== JSON.stringify(users)) {
    setLocalUsers(users);
  }
}, [users]);

  // merge followedUsers + API users
  const suggestedUsers = [...followedUsers, ...localUsers]
    .filter(
      (user, index, self) =>
        index === self.findIndex((u) => u.id === user.id)
    )
    .slice(0, 5);

  const handleProfileClick = useCallback(
    (userId) => {
      if (!userId) return;
      navigate(`/profile/${userId}`);
    },
    [navigate]
  );

  const handleFollow = async (e, user) => {
    e.stopPropagation();

    try {
      setLoadingUser(user.id);

      if (user.isFollowing) {

        await unfollowUser(user.id).unwrap();

        // update local followed users
        setFollowedUsers(prev =>
          prev.map(u =>
            u.id === user.id
              ? { ...u, isFollowing: false }
              : u
          )
        );

        setLocalUsers(prev =>
          prev.map(u =>
            u.id === user.id
              ? { ...u, isFollowing: false }
              : u
          )
        );

      } else {

        await followUser(user.id).unwrap();

        // keep user visible even after API refetch
        setFollowedUsers(prev => [
          { ...user, isFollowing: true },
          ...prev
        ]);

        setLocalUsers(prev =>
          prev.map(u =>
            u.id === user.id
              ? { ...u, isFollowing: true }
              : u
          )
        );
      }

    } catch (err) {
      console.error("Follow action failed", err);
    } finally {
      setLoadingUser(null);
    }
  };

  if (isLoading) return <p>Loading...</p>;

  return (
    <aside className={styles.rightSidebar}>
      <div className={styles.header}>
        <h2>Suggested for you</h2>

        <span
          className={styles.seeAll}
          onClick={() => navigate("/suggested-users")}
        >
          See All
        </span>
      </div>

      <ul className={styles.userList}>
        {suggestedUsers.map((user) => (
          <li
            key={user.id}
            className={styles.userItem}
            onClick={() => handleProfileClick(user.id)}
          >
            <div className={styles.userLeft}>
              {user.profilePhotoUrl ? (
                <img
                  src={user.profilePhotoUrl}
                  alt={user.name}
                  className={styles.avatar}
                />
              ) : (
                <div className={styles.noPhoto}>
                  {user.name
                    ?.split(' ')
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase() || '?'}
                </div>
              )}

              <div className={styles.userInfo}>
                <span className={styles.userName}>
                  {user.name}
                </span>

                <span className={styles.userType}>
                  {user.userType} {user.userType === 'Rookie' && user.rookieType
              ? ` (${user.rookieType})`
              : ''}
                </span>

                <span className={styles.location}>
                  {user.state} 
                </span>
              </div>
            </div>

            <button
              className={
                user.isFollowing
                  ? styles.unfollowBtn
                  : styles.followBtn
              }
              onClick={(e) => handleFollow(e, user)}
              disabled={loadingUser === user.id}
            >
              {loadingUser === user.id
                ? "..."
                : user.isFollowing
                ? "Following"
                : "Follow"}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}